<template lang="html">
  <div id="root">
    <b-container fluid>
      <b-row>
        <b-form-checkbox-group stacked v-model="otherRoutingZones" :options="otherRoutingZonesList" style="
          position: absolute;
          top: 150px;
          left: 10px;
          background: #fff;
          padding: 20px;
          height: auto;
          max-height: 430px;
          overflow-y: scroll;
          z-index: 1000">
        </b-form-checkbox-group>
        <editable-map
          editable
          ref="map"
          id="map"
          :zoom="12"
          :center="center"
          style="width: 100%; height: 500px"
          @ready="emitReady"
        >
          <l-tile-layer
            v-for="tileProvider in tileProviders"
            :key="tileProvider.name"
            :name="tileProvider.name"
            :visible="tileProvider.visible"
            :url="tileProvider.url"
            :attribution="tileProvider.attribution"
            layer-type="base"
            :options="tileProvider.options"
            v-if="tileProvidersReady"
          />
          <l-control-fullscreen position="bottomleft"
            :options="{title: { false: $gettext('Go fullscreen'), true: $gettext('Back to normal') }}"
          />
          <l-control-layers
            :position="layersPosition"
            :collapsed="false"
            :sort-layers="false"
          />
          <div v-if="paths.length > 0">
            <l-control position="topright" v-for="(path, i) in paths" :key="i">
              <div class="leaflet-bar">
                <a href="#" @click.prevent="removePath(i);" style="position: relative;">▱ {{i+1}}<span style="position: absolute;left: 50%; top:50%; transform: translate(-.7rem, -1rem);font-size: 1.3rem" class="text-danger">×&nbsp;&nbsp;</span></a>
              </div>
            </l-control>
            <l-control position="topright">
              <div class="leaflet-bar">
                <a href="#" @click.prevent="addPath()" style="position: relative;">▱ +</a>
              </div>
            </l-control>
          </div>
          <div v-else>
            <l-control position="topright">
              <div class="leaflet-bar">
                <a href="#" @click.prevent="addPath()" style="position: relative;">▱ +</a>
              </div>
            </l-control>
          </div>
          <editable-polygon
            ref="polygon"
            v-if="paths.length > 0"
            :editable="editablePolygon"
            :lat-lngs="paths"
            :color="routingZone.is_dangerous ? 'red' : '#3388ff'"
            :fillColor="routingZone.is_dangerous ? 'red' : '#3388ff'"
            @editable:editing="updateEdited($event)"
          />
          <editable-polygon
            ref="otherPolygon"
            v-if="otherRoutingZones.length > 0"
            :editable="false"
            :lat-lngs="visibleOtherRoutingZones"
          />
        </editable-map>
      </b-row>
      <b-row>
        <b-col cols="12" class="text-center mt-3">
          <b-button v-b-modal.modalEnterGeoJson v-translate>Copy/Paste Standard GeoJSON</b-button>
        </b-col>
      </b-row>
    </b-container>
    <br>
    <b-container>
      <b-row>
        <b-col>
          <b-alert :show="dismissCountDown" dismissible variant="success" @dismissed="dismissCountDown=0" @dismiss-count-down="countDownChanged">
            <h5 v-translate>Routing Zone was updated!</h5>
          </b-alert>
          <b-card>
            <h1 :class="routingZone.is_dangerous ? 'text-danger' : ''">{{routingZone.name}} <small>({{routingZone.country_iso_code}})</small></h1>
            <b-form>
              <b-form-row>
                <div class="form-group col-12">
                  <label for="change-status" v-translate>Name</label>
                  <div class="input-group">
                    <b-form-input v-model="routingZone.name" type="text" />
                  </div>
                </div>
                <div class="form-group col-12">
                  <label for="change-status" v-translate>Code (for labels, max. 8 chars)</label>
                  <div class="input-group">
                    <b-form-input v-model="routingZone.code" type="text" />
                  </div>
                </div>
                <div class="form-group col-12">
                  <label for="change-status" v-translate>Country (2 letter ISO code)</label>
                  <div class="input-group">
                    <b-form-input v-model="routingZone.country_iso_code" type="text" />
                  </div>
                </div>
              </b-form-row>

              <div class="form-group col-12 form-actions">
                <label class="col-md-3 control-label"></label>
                <div class="col-md-8">
                  <b-button variant="secondary" size="lg" @click.prevent="updateRoutingZone()" v-translate>Save Changes</b-button>
                </div>
              </div>
            </b-form>
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <b-modal id="modalEnterGeoJson" ref="modalEnterGeoJson" @show="editablePolygon = false" @hide="editablePolygon = true" :hide-footer="true">
      <div slot="modal-title" v-translate>Paste a Standard GeoJSON</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <b-form-row>
        <div class="form-group col-12">
          <label class="col-md-3 control-label" v-translate>GeoJSON</label>
          <b-textarea v-model="polygonGeojson" style="width: 100%; height: 200px"
            @input="readGeojson">
          </b-textarea>
        </div>
        <div v-if="errorMessage">{{errorMessage}}</div>
      </b-form-row>
        <b-form-row>
          <div class="text-center form-group col-12 form-actions">
            <b-button type="button" variant="outline-primary" size="lg" @click.prevent="$refs.modalEnterGeoJson.hide()" v-translate>Close</b-button>
          </div>
        </b-form-row>
    </b-modal>
  </div>
</template>

<script>
import { latLngBounds } from 'leaflet'
import {
  LTileLayer,
  LControlLayers,
  LControl
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import { EditableMap, EditablePolygon } from 'vue2-leaflet-editable'
import { SearchControl, OpenStreetMapProvider } from 'leaflet-geosearch'
import 'leaflet-geosearch/assets/css/leaflet.css'
import LControlFullscreen from 'vue2-leaflet-fullscreen'

export default {
  components: {
    LTileLayer,
    LControlLayers,
    LControl,
    EditableMap,
    EditablePolygon,
    LControlFullscreen
  },
  data() {
    return {
      dismissSecs: 10,
      dismissCountDown: 0,
      routingZone: {},
      edited: null,
      paths: [],
      mvcPaths: null,
      errorMessage: null,
      polygonGeojson: '',
      routingZoneUpdated: false,
      otherRoutingZones: [],
      otherRoutingZonesList: [],
      visibleOtherRoutingZones: [],
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      opacity: 1,
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      currentZoom: 11,
      minZoom: 1,
      maxZoom: 18,
      zoomPosition: 'topleft',
      layersPosition: 'topright',
      attributionPosition: 'bottomright',
      imperial: false,
      editablePolygon: false,
      platformRoutingZoneMappings: [
        'meli'
      ],
      platformRoutingZoneMapping: {}
    }
  },
  watch: {
    otherRoutingZones(val) {
      this.visibleOtherRoutingZones = []
      for (let i = 0; i < val.length; i++) {
        this.visibleOtherRoutingZones.push(val[i].coords[0])
      }
    },
    routingZonesWithCoords(val) {
      console.log('ROUTING ZONES WITH COORDS')
      this.otherRoutingZonesList = this.routingZonesWithCoords.filter(x => x.id !== this.routingZone.id)
    }
  },
  created() {
    this.fetchRoutingZones()
    this.checkMapProviders()
  },
  mounted() {
    this.fetchPlatforms()
    this.fetchRoutingZone()
  },
  methods: {
    fetchRoutingZone() {
      if (this.map) {
        this.$nextTick(() => {
          this.$http.get(this.$appConfig.api.baseUrl + 'routing-zones/' + this.$route.params.id + '?includes[]=size_types&includes[]=routingZone_formula&includes[]=platform_routingZones.platform')
            .then(response => {
              this.routingZone = response.body

              this.readGeojson(JSON.stringify(this.routingZone.area))

              this.center = latLngBounds(this.paths.map(o => o.map(x => [x.lat, x.lng]))).getCenter()
              this.fitted = true
            })
        })
      } else {
        setTimeout(function () {
          this.fetchRoutingZone()
        }.bind(this), 100)
      }
    },
    updateRoutingZone() {
      if (this.routingZoneUpdated === false) {
        delete this.routingZone.area
      }
      this.$http.patch(this.$appConfig.api.baseUrl + 'routing-zones/' + this.$route.params.id, this.routingZone).then(response => {
        this.showAlert()
        this.fetchRoutingZone()
      }, error => {
        let obj = {
          title: this.$gettext('ERROR'),
          message: error.body.message,
          type: 'error'
        }
        this.$parent.$refs.simplert.openSimplert(obj)
      })
    },
    updateEdited($event) {
      this.editablePolygon = false
      this.routingZoneUpdated = true

      let latLngs = $event.layer.getLatLngs()
      this.routingZone.area = []
      this.routingZone.area = latLngs.map(p => { return p.map(ll => { return ll.lat + ',' + ll.lng }).join('\n') })

      this.polygonGeojson = JSON.stringify({
        type: 'Polygon',
        coordinates: latLngs.map(p => { return this.closeLoop(p.map(ll => { return [ll.lng, ll.lat] })) })
      })

      this.editablePolygon = true
    },
    addPath () {
      this.routingZoneUpdated = true
      this.editablePolygon = false
      // obtain the bounds, so we can guess how big the polygon should be
      let bounds = this.map.getBounds()
      let northEast = bounds.getNorthEast()
      let southWest = bounds.getSouthWest()
      let center = bounds.getCenter()
      let degree = this.paths.length + 1
      let f = Math.pow(0.66, degree)

      // Draw a triangle. Use f to control the size of the triangle.
      // i.e., every time we add a path, we reduce the size of the triangle
      let path = [
        { lng: center.lng, lat: (1 - f) * center.lat + (f) * northEast.lat },
        { lng: (1 - f) * center.lng + (f) * southWest.lng, lat: (1 - f) * center.lat + (f) * southWest.lat },
        { lng: (1 - f) * center.lng + (f) * northEast.lng, lat: (1 - f) * center.lat + (f) * southWest.lat }
      ]

      this.paths.push(path)
      this.routingZone.area = this.paths.map(p => { return p.map(ll => { return ll.lat + ',' + ll.lng }).join('\n') })
      this.polygonGeojson = JSON.stringify({
        type: 'Polygon',
        coordinates: this.paths.map(p => { return this.closeLoop(p.map(ll => { return [ll.lng, ll.lat] })) })
      })
      this.$nextTick(() => {
        this.editablePolygon = true
      })
    },
    removePath(i) {
      this.routingZoneUpdated = true
      this.editablePolygon = false
      this.paths.splice(i, 1)
      this.routingZone.area = this.paths.map(p => { return p.map(ll => { return ll.lat + ',' + ll.lng }).join('\n') })
      this.polygonGeojson = JSON.stringify({
        type: 'Polygon',
        coordinates: this.paths.map(p => { return this.closeLoop(p.map(ll => { return [ll.lng, ll.lat] })) })
      })

      this.$nextTick(() => {
        this.editablePolygon = true
      })
    },
    readGeojson(geoJson) {
      try {
        this.paths = []
        this.editablePolygon = false
        this.polygonGeojson = geoJson

        let v = JSON.parse(geoJson)

        this.paths = v.coordinates.map(linearRing =>
          linearRing.slice(0, linearRing.length - 1)
            .map(([lng, lat]) => ({lat, lng}))
        )

        this.center = latLngBounds(this.paths.map(o => o.map(x => [x.lat, x.lng]))).getCenter()
        this.fitted = true

        this.$nextTick(() => {
          this.editablePolygon = true
        })
        this.errorMessage = null
      } catch (err) {
        this.errorMessage = err.message
      }
    },
    closeLoop (path) {
      return path.concat(path.slice(0, 1))
    },
    emitReady() {
      this.map = this.$refs.map.mapObject
      this.map.invalidateSize()
      this.map.on('popupclose', () => this.emitCourier(null))
      this.mapReady = true
      this.$emit('ready', true)
      this.editablePolygon = true

      // geosearch control
      const searchControl = new SearchControl({
        style: 'bar',
        provider: new OpenStreetMapProvider(),
        position: 'topright',
        searchLabel: this.$gettext('Enter city')
      })
      this.map.addControl(searchControl)
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    }
  }
}
</script>
